import React from 'react';
import { resolveImageUrl } from '../utils/image';
import useStoreSettings from '../hooks/useStoreSettings';
import {
  Alert,
  Box,
  Button as MuiButton,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Typography
} from '@mui/material';
import { Add, FavoriteBorder, Remove } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  clearMyCart,
  removeCartItem,
  updateCartItem,
  removeGuestCartItem,
  updateGuestCartItem,
  clearGuestCart
} from '../features/cartSlice';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { addToWishlist } from '../features/wishlistSlice';
import { ROUTES } from '../constants';
import { formatCurrency, getEffectivePrice } from '../utils/pricing';
import { CartItem } from '../types';

const CartPage: React.FC = () => {
  useDocumentTitle('Cart');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);
  const { cart, guestItems, loading, mutating, error } = useAppSelector((state) => state.cart);

  const isGuest = !token;
  const lineItems: CartItem[] = isGuest
    ? guestItems.filter((item) => Boolean(item.product))
    : (cart?.items || []).filter((item) => Boolean(item.product));

  const subtotal = lineItems.reduce(
    (sum, item) => sum + getEffectivePrice(item.product) * item.quantity,
    0
  );
  const totalItems = lineItems.reduce((sum, item) => sum + item.quantity, 0);
  const store = useStoreSettings();
  // Mirror the server's delivery rule (see order.service.createOrder)
  const deliveryCharge =
    store && store.shippingCharge > 0 &&
    !(store.freeShippingThreshold > 0 && subtotal >= store.freeShippingThreshold)
      ? store.shippingCharge
      : 0;
  const amountToFreeShipping =
    store && store.shippingCharge > 0 && store.freeShippingThreshold > 0 && subtotal < store.freeShippingThreshold
      ? store.freeShippingThreshold - subtotal
      : 0;

  const updateQuantity = (productId: string, quantity: number) => {
    if (isGuest) {
      dispatch(updateGuestCartItem({ productId, quantity: Math.max(1, quantity) }));
    } else {
      dispatch(updateCartItem({ productId, quantity: Math.max(1, quantity) }));
    }
  };

  const handleRemove = (productId: string) => {
    if (isGuest) {
      dispatch(removeGuestCartItem({ productId }));
    } else {
      dispatch(removeCartItem({ productId }));
    }
  };

  const handleClear = () => {
    if (isGuest) {
      dispatch(clearGuestCart());
    } else {
      dispatch(clearMyCart());
    }
  };

  const moveToWishlist = async (productId: string) => {
    if (!token) return;
    await dispatch(addToWishlist({ productId }));
    await dispatch(removeCartItem({ productId }));
  };

  if (!isGuest && loading && !cart) {
    return <Typography>Loading cart...</Typography>;
  }

  if (lineItems.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Your cart is empty
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Looks like you have not added any products yet.
        </Typography>
        <MuiButton variant="contained" component={RouterLink} to={ROUTES.PRODUCTS}>
          Continue shopping
        </MuiButton>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Shopping Cart
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalItems} item{totalItems > 1 ? 's' : ''} ready for checkout
          </Typography>
        </Box>
        <MuiButton variant="outlined" component={RouterLink} to={ROUTES.PRODUCTS}>
          Continue shopping
        </MuiButton>
      </Box>

      {isGuest && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <RouterLink to={`${ROUTES.LOGIN}?redirect=${encodeURIComponent(ROUTES.CART)}`} style={{ fontWeight: 600 }}>
            Log in
          </RouterLink>{' '}
          to save your cart across devices and access your wishlist.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.5fr 0.7fr' },
          gap: 3,
          alignItems: 'start'
        }}
      >
      <Paper
        variant="outlined"
        sx={{ p: 3, borderRadius: 3, borderColor: 'rgba(0,0,0,0.08)', bgcolor: '#fff' }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {lineItems.map((item, index) => (
          <Box key={item.product._id}>
            <Box
              sx={{
                py: 2.25,
                display: 'grid',
                gridTemplateColumns: { xs: '64px 1fr auto', sm: '84px 1fr auto' },
                gap: 2,
                alignItems: 'center'
              }}
            >
              <Box
                component="img"
                src={resolveImageUrl(item.product.images?.[0] || '')}
                alt={item.product.name}
                onClick={() => navigate(`${ROUTES.PRODUCTS}/${item.product.slug || item.product._id}`)}
                sx={{
                  width: { xs: 64, sm: 84 },
                  height: { xs: 64, sm: 84 },
                  borderRadius: 2,
                  objectFit: 'cover',
                  border: '1px solid rgba(0,0,0,0.06)',
                  cursor: 'pointer'
                }}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  component={RouterLink}
                  to={`${ROUTES.PRODUCTS}/${item.product.slug || item.product._id}`}
                  sx={{
                    fontWeight: 600,
                    textDecoration: 'none',
                    color: 'text.primary',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {item.product.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {formatCurrency(getEffectivePrice(item.product))}
                </Typography>
                <Box sx={{ mt: 1.25, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 999,
                      px: 0.5
                    }}
                  >
                    <IconButton
                      size="small"
                      disabled={mutating || item.quantity <= 1}
                      onClick={() => updateQuantity(item.product._id, item.quantity - 1)}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <Typography sx={{ minWidth: 32, textAlign: 'center', fontWeight: 600 }}>
                      {item.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={mutating}
                      onClick={() => updateQuantity(item.product._id, item.quantity + 1)}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatCurrency(
                      item.quantity * getEffectivePrice(item.product)
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', justifyItems: 'end', gap: 0.5 }}>
                {!isGuest && (
                  <MuiButton
                    size="small"
                    variant="outlined"
                    startIcon={<FavoriteBorder />}
                    disabled={mutating}
                    onClick={() => moveToWishlist(item.product._id)}
                  >
                    Move to wishlist
                  </MuiButton>
                )}
                <MuiButton
                  size="small"
                  variant="text"
                  color="inherit"
                  disabled={mutating}
                  sx={{ color: 'text.secondary', textTransform: 'none', minWidth: 'auto', px: 0.5 }}
                  onClick={() => handleRemove(item.product._id)}
                >
                  Remove
                </MuiButton>
              </Box>
            </Box>
            {index < lineItems.length - 1 && <Divider />}
          </Box>
        ))}
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          height: 'fit-content',
          borderRadius: 3,
          borderColor: 'rgba(0,0,0,0.08)',
          position: { lg: 'sticky' },
          top: { lg: 92 }
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Order Summary
        </Typography>

        {/* Free-shipping goal — the hero, not the fee */}
        {store && store.shippingCharge > 0 && store.freeShippingThreshold > 0 && (
          <Box
            sx={{
              mb: 2.5,
              p: 1.75,
              borderRadius: 2,
              background:
                amountToFreeShipping > 0
                  ? 'linear-gradient(135deg, rgba(214,90,136,0.08), rgba(112,97,218,0.08))'
                  : 'rgba(46,125,50,0.08)'
            }}
          >
            {amountToFreeShipping > 0 ? (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  🎉 You're only{' '}
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    {formatCurrency(amountToFreeShipping)}
                  </Box>{' '}
                  away from FREE shipping!
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (subtotal / store.freeShippingThreshold) * 100)}
                  sx={{
                    height: 8,
                    borderRadius: 99,
                    backgroundColor: 'rgba(43,37,43,0.08)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 99,
                      background: 'linear-gradient(90deg, #D65A88, #7061DA)'
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(subtotal)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(store.freeShippingThreshold)}
                  </Typography>
                </Box>
                <MuiButton
                  size="small"
                  variant="text"
                  component={RouterLink}
                  to={ROUTES.PRODUCTS}
                  sx={{ mt: 0.5, px: 0 }}
                >
                  Add something sparkly →
                </MuiButton>
              </>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                🎉 You've unlocked FREE shipping!
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Subtotal
          </Typography>
          <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Items
          </Typography>
          <Typography variant="body2">{totalItems}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Standard Shipping
          </Typography>
          {deliveryCharge > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {formatCurrency(deliveryCharge)}
            </Typography>
          ) : (
            <Typography variant="body2" color="success.main">
              Free
            </Typography>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1">Total</Typography>
          <Typography variant="subtitle1">{formatCurrency(subtotal + deliveryCharge)}</Typography>
        </Box>
        {isGuest ? (
          <MuiButton
            fullWidth
            variant="contained"
            onClick={() => navigate(`${ROUTES.LOGIN}?redirect=${encodeURIComponent(ROUTES.CHECKOUT)}`)}
          >
            Log in to checkout
          </MuiButton>
        ) : (
          <MuiButton
            fullWidth
            variant="contained"
            disabled={mutating}
            component={RouterLink}
            to={ROUTES.CHECKOUT}
          >
            Proceed to checkout
          </MuiButton>
        )}
        <MuiButton
          fullWidth
          variant="text"
          color="error"
          sx={{ mt: 1 }}
          onClick={handleClear}
          disabled={!isGuest && mutating}
        >
          Clear cart
        </MuiButton>
      </Paper>
    </Box>
    </Box>
  );
};

export default CartPage;
